import { useEffect, useState } from "react";
import { settings } from "../lib/api";
import type { SystemInfo } from "../lib/api";
import { RefreshCw, Bell, Save, TestTube } from "lucide-react";

export default function Settings() {
  const [system, setSystem] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [discordWebhook, setDiscordWebhook] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchData = async () => {
    try {
      const [sysInfo, allSettings] = await Promise.all([
        settings.system(),
        settings.get(),
      ]);
      setSystem(sysInfo);
      if (allSettings.discord_webhook_url) {
        setDiscordWebhook(allSettings.discord_webhook_url as string);
      }
    } catch (error) {
      console.error("Failed to fetch settings", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveWebhook = async () => {
    setSaving(true);
    try {
      await settings.set("discord_webhook_url", discordWebhook);
      alert("Saved!");
    } catch (error) {
      alert("Failed: " + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    try {
      await settings.testDiscord(discordWebhook);
      alert("Test notification sent!");
    } catch (error) {
      alert("Failed: " + (error as Error).message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* System Info */}
      <div className="card">
        <h3 className="font-display text-lg font-semibold mb-4">System Information</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Node Version</p>
            <p className="font-mono">{system?.nodeVersion}</p>
          </div>
          <div>
            <p className="text-gray-400">Uptime</p>
            <p>{system ? Math.floor(system.uptime / 3600) : 0}h {system ? Math.floor((system.uptime % 3600) / 60) : 0}m</p>
          </div>
          <div>
            <p className="text-gray-400">Memory Used</p>
            <p>{system ? Math.round(system.memory.heapUsed / 1024 / 1024) : 0} MB</p>
          </div>
          <div>
            <p className="text-gray-400">Memory Total</p>
            <p>{system ? Math.round(system.memory.heapTotal / 1024 / 1024) : 0} MB</p>
          </div>
        </div>
      </div>

      {/* Discord Notifications */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="text-primary-500" size={24} />
          <h3 className="font-display text-lg font-semibold">Discord Notifications</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Webhook URL</label>
            <input
              type="url"
              className="input font-mono text-sm"
              placeholder="https://discord.com/api/webhooks/..."
              value={discordWebhook}
              onChange={(e) => setDiscordWebhook(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSaveWebhook}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
              Save
            </button>
            <button
              onClick={handleTestWebhook}
              disabled={testing || !discordWebhook}
              className="btn-secondary flex items-center gap-2"
            >
              {testing ? <RefreshCw size={18} className="animate-spin" /> : <TestTube size={18} />}
              Test
            </button>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="card">
        <h3 className="font-display text-lg font-semibold mb-4">About EZPoke</h3>
        <p className="text-sm text-gray-400">
          Pokemon TCG stock monitor and auto-checkout bot. Built for speed and reliability.
        </p>
        <p className="text-xs text-gray-500 mt-2">Version 1.0.0</p>
      </div>
    </div>
  );
}

